
import { supabase } from "@/integrations/supabase/client"
import { toast } from "@/hooks/use-toast"

interface GenerateDocumentParams {
  type: "oficio" | "requerimento" | "discurso" | "memorando"
  formData: {
    subject: string
    content: string
    recipient: string
    officeType?: string
    requestType?: string
    eventType?: string
    targetAudience?: string
    speechObjective?: string
    memorandumType?: string
  }
  profile: any
}

export function useDocumentGeneration() {
  const generateDocument = async ({ type, formData, profile }: GenerateDocumentParams) => {
    try {
      if (!formData.content || 
        (type === "requerimento" && !formData.requestType) ||
        (type === "memorando" && !formData.memorandumType)) {
        toast({
          title: "Campos obrigatórios",
          description: "Por favor, preencha todos os campos obrigatórios.",
          variant: "destructive",
          duration: 3000,
        })
        return null
      }

      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        throw new Error("Usuário não encontrado")
      }

      const { data: userProfile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single()

      if (profileError) {
        throw new Error("Erro ao buscar perfil do usuário")
      }

      const currentDate = new Intl.DateTimeFormat('pt-BR', {
        timeZone: 'America/Sao_Paulo',
        dateStyle: 'full',
        timeStyle: 'long'
      }).format(new Date())

      const response = await supabase.functions.invoke('generate-document', {
        body: {
          type,
          requestType: formData.requestType,
          content: formData.content,
          subject: formData.subject,
          recipient: formData.recipient,
          officeType: formData.officeType,
          eventType: formData.eventType,
          targetAudience: formData.targetAudience,
          speechObjective: formData.speechObjective,
          memorandumType: formData.memorandumType,
          userId: user.id,
          userProfile: {
            name: `${userProfile?.first_name || ''} ${userProfile?.last_name || ''}`.trim(),
            city: userProfile?.city || '',
            state: userProfile?.state || '',
            role: userProfile?.role || 'councilor',
            prefeito: userProfile?.prefeito || '',
          },
          currentDate,
        },
      })

      if (response.error) {
        if (response.error.message?.includes('OpenAI')) {
          toast({
            title: "Erro na geração do documento",
            description: "Houve um problema na comunicação com o serviço de IA. Por favor, aguarde alguns instantes e tente novamente.",
            variant: "destructive",
            duration: 5000,
          })
          return null
        }
        throw new Error(response.error.message || "Erro ao gerar documento")
      }

      if (!response.data?.generatedText) {
        toast({
          title: "Processo em andamento",
          description: "Por favor, aguarde alguns instantes e tente novamente. O sistema está processando sua solicitação.",
          duration: 5000,
        })
        return null
      }

      let formattedText = response.data.generatedText;
      
      // Remover marcações indesejadas que possam estar vindo da API
      formattedText = formattedText.replace(/~html|~$/g, '');
      formattedText = formattedText.replace(/```html|```/g, '');
      
      // Aplicar a formatação HTML
      formattedText = formatOpenAIText(formattedText);

      const { error: saveError } = await supabase
        .from("documents")
        .insert({
          user_id: user.id,
          title: formData.subject || `${type} - ${new Date().toLocaleDateString()}`,
          content: formattedText,
          type,
          user_name: `${userProfile?.first_name || ''} ${userProfile?.last_name || ''}`.trim(),
          user_city: userProfile?.city || '',
          user_state: userProfile?.state || '',
        })

      if (saveError) throw saveError

      toast({
        title: "Documento gerado e salvo com sucesso!",
        description: "O documento foi gerado e salvo em sua biblioteca.",
        duration: 3000,
      })

      return formattedText
    } catch (error) {
      console.error("Erro ao gerar documento:", error)
      
      const errorMessage = error instanceof Error ? error.message : "Erro desconhecido"
      if (errorMessage.includes('OpenAI')) {
        toast({
          title: "Erro na geração do documento",
          description: "Houve um problema na comunicação com o serviço de IA. Por favor, aguarde alguns instantes e tente novamente.",
          variant: "destructive",
          duration: 5000,
        })
      } else {
        toast({
          title: "Erro ao gerar documento",
          description: "Ocorreu um erro ao gerar o documento. Por favor, tente novamente.",
          variant: "destructive",
          duration: 3000,
        })
      }
      return null
    }
  }

  const formatOpenAIText = (text: string): string => {
    if (!text) return '';

    let formattedText = text;

    // Remover tags indesejadas que possam vir da API
    formattedText = formattedText.replace(/~html|~$/g, '');
    formattedText = formattedText.replace(/```html|```/g, '');

    // Formatação básica
    formattedText = formattedText.replace(/\[b\](.*?)\[\/b\]/gs, '<strong>$1</strong>');
    formattedText = formattedText.replace(/\*\*(.*?)\*\*/gs, '<strong>$1</strong>');
    
    formattedText = formattedText.replace(/\[i\](.*?)\[\/i\]/gs, '<em>$1</em>');
    formattedText = formattedText.replace(/\*(.*?)\*/gs, '<em>$1</em>');
    
    formattedText = formattedText.replace(/\[h1\](.*?)\[\/h1\]/gs, '<h1>$1</h1>');
    formattedText = formattedText.replace(/# (.*?)($|\n)/gs, '<h1>$1</h1>');
    
    formattedText = formattedText.replace(/\[h2\](.*?)\[\/h2\]/gs, '<h2>$1</h2>');
    formattedText = formattedText.replace(/## (.*?)($|\n)/gs, '<h2>$1</h2>');
    
    formattedText = formattedText.replace(/\[h3\](.*?)\[\/h3\]/gs, '<h3>$1</h3>');
    formattedText = formattedText.replace(/### (.*?)($|\n)/gs, '<h3>$1</h3>');
    
    // Quebras de linha
    formattedText = formattedText.replace(/\[br\]/g, '<br>');
    formattedText = formattedText.replace(/\n/g, '<br>');
    
    // Parágrafos
    formattedText = formattedText.replace(/\[p\](.*?)\[\/p\]/gs, '<p>$1</p>');
    
    // Listas
    formattedText = formattedText.replace(/\[ul\](.*?)\[\/ul\]/gs, '<ul>$1</ul>');
    formattedText = formattedText.replace(/\[ol\](.*?)\[\/ol\]/gs, '<ol>$1</ol>');
    formattedText = formattedText.replace(/\[li\](.*?)\[\/li\]/gs, '<li>$1</li>');
    
    // Alinhamentos
    formattedText = formattedText.replace(/\[center\](.*?)\[\/center\]/gs, '<div style="text-align: center">$1</div>');
    formattedText = formattedText.replace(/\[right\](.*?)\[\/right\]/gs, '<div style="text-align: right">$1</div>');
    formattedText = formattedText.replace(/\[justify\](.*?)\[\/justify\]/gs, '<div style="text-align: justify">$1</div>');
    
    // Processar quaisquer tags restantes
    const remainingTags = formattedText.match(/\[\w+\].*?\[\/\w+\]/gs);
    if (remainingTags) {
      remainingTags.forEach(tag => {
        const tagName = tag.match(/\[(\w+)\]/)?.[1];
        if (tagName) {
          const content = tag.replace(`[${tagName}]`, '').replace(`[/${tagName}]`, '');
          formattedText = formattedText.replace(tag, `<div class="${tagName}">${content}</div>`);
        }
      });
    }
    
    return formattedText;
  };

  return { generateDocument }
}
